import { safeStorage } from "../../../../../shared/lib/safeStorage";
import { CHECKOUT_MARKER_MAX_MS, currentGuestIdentity, isCheckoutScope, type CheckoutScope } from "../../lib/guestSession";

/**
 * THIS TAB'S CHECKOUT IN FLIGHT (the Web Director's H3 ruling; design §2.7):
 * the checkout reference and the professional it is for, in sessionStorage,
 * so that Back from Stripe onto a freshly loaded Review, or the return page
 * after its `ref` was scrubbed from the address bar, can still read
 * `checkoutStatus(ref)` and offer to resume instead of a blind block.
 *
 * - `ref` answers only for the uid that started the checkout (Backend rev 3,
 *   H5 final: the bound uid gains nothing beyond that ref's status and
 *   resume), so holding it in this tab adds no reach.
 * - Same tab only (sessionStorage survives the round trip to Stripe). Never
 *   localStorage, never the URL after the scrub, never logged or reported.
 * - Lifetime at most `until` (the marker's bound, ≤ 30 min from the write);
 *   a record past it reads as none and is removed. Cleared on any terminal
 *   status read.
 * - The checkout URL is NOT kept: a resume always takes a fresh one from
 *   `checkoutStatus` (design §2.6).
 * - v2 (security code review of §2.4 (a), (b)): it names the anonymous
 *   identity it was kept for (its creation instant), because the record is
 *   per TAB while the identity is per DEVICE. A record of an earlier
 *   identity must not lift a later identity's block. No identity, no record.
 * - v3 (W4 F1, the security auditor, 2026-10-20): it names its SCOPE, a
 *   booking's professional (`p`) or a management link's appointment (`a`).
 *   Only a `p` record names the checkout that blocks Review. A v2 record,
 *   which has no kind, reads as none and is removed.
 */

const KEY = "lura.book.checkout";
/** Conservative until the contract pins the shape: an opaque id. */
const REF = /^[A-Za-z0-9_-]{8,128}$/;
export interface CheckoutRecord {
  readonly ref: string;
  /** What the checkout is for: a booking's professional, or an /a appointment. */
  readonly scope: CheckoutScope;
  /** The anonymous identity's creation instant it was kept for. */
  readonly identity: number;
  readonly until: number;
}

/** A well-formed checkout reference (from the return URL or this record). */
export function isCheckoutRef(value: unknown): value is string {
  return typeof value === "string" && REF.test(value);
}

/**
 * Keep this tab's checkout for the CURRENT anonymous identity, until `until`
 * (capped at 30 min from now). False when refused, malformed, or there is no
 * known anonymous identity.
 */
export function keepCheckoutRecord(ref: string, scope: CheckoutScope, until: number, now: number = Date.now()): boolean {
  const identity = currentGuestIdentity();
  if (identity === null) return false;
  if (!isCheckoutRef(ref) || !isCheckoutScope(scope) || !Number.isFinite(until) || until <= now) return false;
  const capped = Math.floor(Math.min(until, now + CHECKOUT_MARKER_MAX_MS));
  return safeStorage.session.set(KEY, JSON.stringify({ v: 3, ref, kind: scope.kind, id: scope.id, identity, until: capped }));
}

/** This tab's live checkout, or null (none, malformed, expired: those are removed). */
export function readCheckoutRecord(now: number = Date.now()): CheckoutRecord | null {
  const raw = safeStorage.session.get(KEY);
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = null;
  }
  const record = parsed as Partial<{ v: unknown; ref: unknown; kind: unknown; id: unknown; identity: unknown; until: unknown }> | null;
  const scope = record !== null && typeof record === "object" ? { kind: record.kind, id: record.id } : null;
  const valid =
    record !== null &&
    typeof record === "object" &&
    record.v === 3 &&
    isCheckoutScope(scope) &&
    typeof record.identity === "number" &&
    Number.isSafeInteger(record.identity) &&
    isCheckoutRef(record.ref) &&
    typeof record.until === "number" &&
    Number.isFinite(record.until) &&
    record.until > now &&
    record.until <= now + CHECKOUT_MARKER_MAX_MS;
  if (!valid) {
    clearCheckoutRecord();
    return null;
  }
  return { ref: record.ref as string, scope: scope as CheckoutScope, identity: record.identity as number, until: record.until as number };
}

/** On a terminal status read, a sign-out, or a fresh attempt. */
export function clearCheckoutRecord(): void {
  safeStorage.session.remove(KEY);
}
